import classnames from 'classnames/bind';
import { useLocation, useParams } from 'react-router-dom';
import { useToggle } from '@mantine/hooks';

import { CameraParams } from 'types/route_params';
import { Stream } from 'types/stream';
import { VideoSource } from 'hooks/api/useDeploymentsVideoSources';

import { useCamera } from 'cameras/hooks/useCamera';
import { useCurrentPlan } from 'organizations/hooks/useCurrentPlan';
import { useHasAccess } from 'hooks/useHasAccess';
import { useMqttStreamsState } from 'streams/hooks/useMqttStreamsState';
import { useStreams } from 'streams/hooks/useStreams';
import { useUpdateCamera } from 'cameras/hooks/useUpdateCamera';

import { NotFound } from 'views/NotFound/NotFound';

import { DeploymentListSmall } from 'components/DeploymentListSmall/DeploymentListSmall';
import {
  DetailView,
  DetailViewHeader,
  DetailViewStatus,
} from 'components/DetailView/DetailView';
import { Loader } from 'components/Loader/Loader';
import { Tabs, TabsButton, TabsContent, TabsList } from 'components/Tabs/Tabs';
import { VideoSourceSnapshot } from 'components/VideoSourceSnapshot/VideoSourceSnapshot';
import { FileList } from './components/FileList/FileList';
import { SharedStreamsList } from 'streams/components/SharedStreams/SharedStreamsList';

import { CameraDetailEntities } from './CameraDetailEntities';
import { CameraDetailStatus } from './CameraDetailStatus';
import { CameraDetailSettings } from './components/CameraDetailSettings/CameraDetailSettings';
import { CameraDetailStreams } from './components/CameraDetailStreams/CameraDetailStreams';

import styles from './CameraDetail.module.scss';

const c = classnames.bind(styles);

export function CameraDetail() {
  const { state } = useLocation();
  const { applicationID, cameraID } = useParams<CameraParams>();
  const { isRingPlan } = useCurrentPlan();

  const defaultTab = state?.tab ?? 'preview';

  const [hasAccess] = useHasAccess();
  const [isRenaming, toggleIsRenaming] = useToggle();

  const { data: camera, isLoading: isCameraLoading } = useCamera(cameraID);
  const { mutate: updateCamera } = useUpdateCamera();

  const { data: streams, isLoading: isStreamsLoading } = useStreams({
    params: {
      sources: ['camera_stream', 'uri_stream'],
      camera_ids: cameraID ? [cameraID] : [],
    },
    enabled: Boolean(cameraID),
  });

  useMqttStreamsState(streams ? streams.data.map(({ id }) => id) : []);

  if (isCameraLoading) {
    return <Loader text="Loading camera..." />;
  }

  const streamIds = streams?.data.map((stream) => stream.id) ?? [];

  let previewSource: VideoSource | null = null;
  if (camera?.conn_type === 'virtual') {
    // if this is a virtual camera, use the first camera stream source as the preview source.
    // for virtual cameras this is the most recent uploaded file (uri_stream) by the SMTP gateway.
    // this is deliberately not testing for `&& (pages?.data?.length ?? 0 > 0)` in the `if` below to set
    // the snapshot source to be null for virtual cameras in that case. This triggers an apropriate
    // error in the <VideoSourceSnapshot /> component.

    previewSource = streams?.data[0] as VideoSource | null;
  } else if (camera?.conn_type === 'local') {
    // If it's a local camera, use the camera itself as preview source since it won't have any streams.
    previewSource = camera;
  } else {
    const isH264 = (stream: Stream) =>
      stream.name.toLowerCase().includes('h264') ||
      stream.name.toLowerCase().includes('h.264') ||
      stream.source === 'pipeline_stream';

    // If this is not a virtual camera, use the first h264 stream as the preview stream if available, otherwise use the first stream.
    const h264Streams = streams?.data.filter(isH264) || [];
    previewSource =
      (h264Streams[0] as VideoSource) ||
      (streams?.data[0] as VideoSource) ||
      null;
  }

  if (isStreamsLoading || isCameraLoading) {
    return (
      <div className={c('loader')}>
        <Loader size="small" text="Loading..." />
      </div>
    );
  }

  if (!camera) {
    return (
      <NotFound
        entity="camera"
        returnTo={`/applications/${applicationID}/deploy/cameras`}
      />
    );
  }

  function handleRename(name: string) {
    if (!camera) {
      return;
    }

    updateCamera({ id: camera.id, name });
  }

  if (!cameraID) {
    return null;
  }

  const isRemote = camera.conn_type === 'remote';
  const isVirtual = camera.conn_type === 'virtual';

  return (
    <DetailView
      key={camera.id} // Key is required to correctly re-initialize detail view
      size="default"
    >
      <DetailViewHeader
        entity={camera}
        entityType="cameras"
        singleQueryKey={['camera']}
        isRenaming={isRenaming}
        onRename={hasAccess('deploy_edit') ? handleRename : undefined}
      >
        <CameraDetailEntities camera={camera} />
        <DetailViewStatus entity={camera} onRename={toggleIsRenaming}>
          <CameraDetailStatus camera={camera} />
        </DetailViewStatus>
      </DetailViewHeader>

      <Tabs defaultValue={defaultTab} className={c('tabs')}>
        <TabsList className={c('tabs-list')}>
          <TabsButton value="preview">Preview</TabsButton>
          {/* Show deployments tab for non-virtual cameras or non-Ring plans */}
          {(!isVirtual || !isRingPlan) && (
            <TabsButton value="deployments">Workflows</TabsButton>
          )}

          {isRemote && (
            <>
              <TabsButton value="streams">Camera Streams</TabsButton>
              <TabsButton value="shared-streams">Shared streams</TabsButton>
            </>
          )}
          {isVirtual && <TabsButton value="uploads">Camera Uploads</TabsButton>}

          {hasAccess('deploy_edit') && (
            <TabsButton value="settings">Settings</TabsButton>
          )}
        </TabsList>

        <TabsContent className={c('tab-content')} value="preview">
          <div className={c('preview')}>
            <VideoSourceSnapshot source={previewSource} camera={camera} />
          </div>
        </TabsContent>

        {isRemote && (
          <>
            <TabsContent className={c('tab-content')} value="streams">
              <CameraDetailStreams camera={camera} />
            </TabsContent>

            <TabsContent className={c('tab-content')} value="shared-streams">
              <SharedStreamsList params={{ camera_ids: [camera.id] }} />
            </TabsContent>
          </>
        )}
        {isVirtual && (
          <TabsContent className={c('tab-content')} value="uploads">
            <FileList cameraID={cameraID} />
          </TabsContent>
        )}

        {/* Show deployments tab for non-virtual cameras or if simplified UX is disabled */}
        {(!isVirtual || !isRingPlan) && (
          <TabsContent className={c('tab-content')} value="deployments">
            <DeploymentListSmall
              streamIds={streamIds}
              cameraId={cameraID}
              hideDeployButton={isVirtual}
            />
          </TabsContent>
        )}

        {hasAccess('deploy_edit') && (
          <TabsContent className={c('tab-content')} value="settings">
            <CameraDetailSettings camera={camera} streamIds={streamIds} />
          </TabsContent>
        )}
      </Tabs>
    </DetailView>
  );
}
